import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Box, Button, Icon, IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid";

import { Consortium } from "../../models/consortium/consortium";
import { ListParams } from "../../models/listParams";
import { consortiumService } from "../../services/consortium.service";
import { portfolioService } from "../../services/portfolio.service";
import { authService } from "../../services/auth.service";

import DialogConsortium from "../controls/dialogs/DialogConsortium";
import DialogConsortiumMembers from "../controls/dialogs/DialogConsortiumMembers";
import ConsortiumsActionMenu from "./ConsortiumsActionMenu";
import ProgressBar from "../controls/ProgressBar";
import ErrorMessage from "../controls/ErrorMessage";

const OverzichtConsortiums: React.FC = () => {
  const navigate = useNavigate();

  const [consortiums, setConsortiums] = useState<Consortium[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("name asc");
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [userRole, setUserRole] = useState<string>("");
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openMembersDialog, setOpenMembersDialog] = useState(false);
  const [selectedConsortiumId, setSelectedConsortiumId] = useState<string | null>(null);

  useEffect(() => {
    const loadRole = async () => {
      const [role, admin] = await Promise.all([
        authService.getRole(),
        authService.isAdmin(),
      ]);
      setUserRole(role);
      setIsAdmin(admin);
      setReady(true);
    };

    loadRole();
  }, []);

  const fetchConsortiums = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ListParams = {
        _offset: pageIndex * pageSize,
        _limit: pageSize,
        _search: searchText || undefined,
        _sort: sortBy || undefined,
      };

      let data: { items: Consortium[]; count: number };
      if (isAdmin) {
        const paged = await consortiumService.getConsortiums(params);
        data = { items: paged.items ?? [], count: paged.count ?? 0 };
      }
      else {
        const paged = await portfolioService.getConsortiums(params);
        data = { items: paged.items ?? [], count: paged.count ?? 0 };
      }
      setConsortiums(data.items);
      setTotalCount(data.count);
    }
    catch (err: any) {
      if (err.message === "Unable to refresh token") {
        navigate("/verlopen");
      }
      setError(err.message || "Er is een fout opgetreden");
    }
    finally {
      setLoading(false);
    }
  }, [navigate, isAdmin, pageIndex, pageSize, searchText, sortBy]);

  useEffect(() => {
    if (!ready) return;
    fetchConsortiums();
  }, [ready, fetchConsortiums, refreshCounter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput.trim());
      setPageIndex(0);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const onChangePagination = ({ page, pageSize }: { page: number; pageSize: number }) => {
    setPageIndex(page);
    setPageSize(pageSize);
  };

  const onChangeSorting = (sortModel: GridSortModel) => {
    if (!sortModel.length || !sortModel[0].field || !sortModel[0].sort) {
      setSortBy("");
      return;
    }

    const { field, sort } = sortModel[0];
    setSortBy(`${field} ${sort}`);
    setPageIndex(0);
  };

  const clearSearch = () => {
    setSearchInput("");
  };

  const triggerRefresh = () => {
    setRefreshCounter((prev) => prev + 1);
  };

  const handleSubmitConsortium = useCallback(async (consortium: Consortium) => {
    setLoading(true);
    setError(null);

    try {
      let response: any;
      if (consortium.id) {
        response = await consortiumService.updateConsortium(consortium);
      }
      else {
        response = await consortiumService.createConsortium(consortium);
      }

      if (response?.error) {
        // Probeer detail uit response of uit message (als JSON)
        let detail = response.detail;
        if (!detail && typeof response.message === 'string') {
          try {
            const parsed = JSON.parse(response.message);
            if (parsed && typeof parsed.detail === 'string') {
              detail = parsed.detail;
            }
          } catch {}
        }
        setError(detail || response.message || "Er is een fout opgetreden");
        return;
      }

      setOpenAddDialog(false);
      triggerRefresh();
    }
    catch (err: any) {
      setError(err.message || "Er is een fout opgetreden");
    }
    finally {
      setLoading(false);
    }
  }, []);

  const openMembers = (consortiumId?: string | null) => {
    if (!consortiumId) return;
    setSelectedConsortiumId(consortiumId);
    setOpenMembersDialog(true);
  };

  const columns = useMemo<GridColDef<Consortium>[]>(
    () => [
      // { field: "id", headerName: "Id", flex: 2 },
      { field: "name", headerName: "Naam", flex: 2 },
      { field: "description", headerName: "Omschrijving", flex: 3 },
      {
        field: "members",
        headerName: "Aantal organisaties",
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<Consortium>) => {
          const memberCount = params.row.members?.length ?? 0;
          const tooltipText = isAdmin
            ? "Organisatie beheren"
            : `Toon de ${memberCount} organisaties`;
          return (
            <Tooltip title={tooltipText} placement="right" arrow>
              <IconButton size="small" onClick={() => openMembers(params.row.id)}>
                <Badge badgeContent={memberCount} color={memberCount === 0 ? "secondary" : "primary"} showZero>
                  <Icon fontSize="small">groups</Icon>
                </Badge>
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: "actions",
        headerName: "Acties",
        renderHeader: () => <></>,
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<Consortium>) => (
          <ConsortiumsActionMenu
            params={params}
            onSubmitConsortium={handleSubmitConsortium}
            onConsortiumsUpdate={triggerRefresh}
          />
        ),
      },
    ],
    [handleSubmitConsortium, isAdmin]
  );

  return (
    <>
      {isAdmin && (
        <Box sx={{ mb: 2 }}>
          <Tooltip title="Samenwerkingsverband toevoegen" placement="right" arrow>
            <Button variant="contained" color="secondary" onClick={() => setOpenAddDialog(true)}>
              <Icon sx={{ mr: 1 }}>add</Icon>
              Samenwerkingsverband
            </Button>
          </Tooltip>
        </Box>
      )}

      <div className="grid-responsive" style={{ display: "flex", flexDirection: "column" }}>
        <div
          className="search"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "8px",
          }}
        >
          <TextField
            id="search-consortiums"
            label="Zoeken in samenwerkingsverbanden"
            variant="filled"
            placeholder="Zoeken op naam of omschrijving"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch} sx={{ p: 0.5 }}>
                    <Icon fontSize="small">clear</Icon>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: { xs: "100%", md: "35%" },
              backgroundColor: "#fff",
              "& .MuiFilledInput-root": {
                backgroundColor: "#fff",
                borderRadius: "4px",
                "&:hover": { backgroundColor: "#fff" },
                "&.Mui-focused": { backgroundColor: "#fff" },
              },
              "& .MuiFilledInput-input": {
                paddingRight: "32px",
              },
            }}
          />
        </div>

        <DataGrid
          rows={consortiums}
          columns={columns}
          getRowId={(row) => row.id || ""}
          disableColumnFilter
          disableRowSelectionOnClick
          rowCount={totalCount}
          paginationMode="server"
          sortingMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                page: pageIndex,
                pageSize,
              },
            },
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          onPaginationModelChange={onChangePagination}
          onSortModelChange={onChangeSorting}
          sx={{ border: 0 }}
        />
      </div>

      <DialogConsortium
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSubmit={handleSubmitConsortium}
      />

      <DialogConsortiumMembers
        open={openMembersDialog}
        consortiumId={selectedConsortiumId}
        onClose={() => setOpenMembersDialog(false)}
        onUpdated={triggerRefresh}
        readOnly={userRole === "User" || userRole === "KeyUser" || userRole === "ReadOnly"}
        members={consortiums.find(c => c.id === selectedConsortiumId)?.members}
      />

      {loading && <ProgressBar />}
      {error && <ErrorMessage message={error} />}
    </>
  );
};

export default OverzichtConsortiums;
